import { Ban, Clock } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface OrganizationBlockedProps {
  title: string;
  message: string;
  hint?: string;
  icon?: ReactNode;
  /** Opcjonalna akcja pod tekstem (np. wylogowanie na ekranie konta tylko do Staff). */
  action?: ReactNode;
}

/**
 * Komunikat pokazywany ZAMIAST treści widoku, gdy panel jest zablokowany
 * (wyłączona organizacja, wygasły plan). Nawigacja zostaje na miejscu —
 * użytkownik ma się dowiedzieć, co się stało, a nie utknąć na pustym ekranie
 * albo zostać wyrzucony z aplikacji.
 */
export const OrganizationBlocked: FC<OrganizationBlockedProps> = ({
  title,
  message,
  hint,
  icon,
  action,
}) => (
  <div className="w-full flex items-center justify-center min-h-[60vh] px-4">
    <div className="max-w-2xl w-full text-left">
      {/* Ikona w stylu sidebara: cienka linia lucide, ciemna, bez tła. */}
      <div className="mb-4 text-text-primary [&_svg]:w-7 [&_svg]:h-7" aria-hidden>
        {icon ?? <Ban strokeWidth={1.5} />}
      </div>
      <h1 className="text-[28px] font-semibold leading-tight text-text-primary mb-2">{title}</h1>
      <p className="text-[14px] text-text-secondary mb-1">{message}</p>
      {hint && <p className="text-[14px] text-text-secondary">{hint}</p>}
      {action}
    </div>
  </div>
);

/** Wariant dla wygasłego okresu próbnego (dawniej: ekran wyboru planu). */
export const TrialExpired: FC = () => {
  const { t } = useTranslation();

  return (
    <OrganizationBlocked
      title={t('trialExpired.title', 'Okres próbny zakończony')}
      message={t(
        'trialExpired.message',
        'Twój okres próbny dobiegł końca i panel jest obecnie niedostępny.',
      )}
      hint={t(
        'trialExpired.contact',
        'Skontaktuj się z opiekunem konta HotelSpot, aby przedłużyć dostęp.',
      )}
      icon={<Clock strokeWidth={1.5} />}
    />
  );
};

/** Wariant dla `organizations/{id}.isDisabled === true`. */
export const OrganizationDisabled: FC = () => {
  const { t } = useTranslation();

  return (
    <OrganizationBlocked
      title={t('organizationDisabled.title', 'Organizacja wyłączona')}
      message={t(
        'organizationDisabled.message',
        'Ta organizacja została wyłączona i panel jest obecnie niedostępny.',
      )}
      hint={t(
        'organizationDisabled.contact',
        'Skontaktuj się z opiekunem konta HotelSpot, jeśli potrzebujesz pomocy.',
      )}
    />
  );
};
